import React, { useMemo, useState } from "react";
import { SwapOutlined } from "@ant-design/icons";
import { Button, Empty, List } from "antd";
import { useTranslation } from "react-i18next";

import { TraineeBodyPhotosCompareModal } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeBodyPhotos/compare/TraineeBodyPhotosCompareModal";
import TraineeBodyPhotoRow from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeBodyPhotos/TraineeBodyPhotoRow";
import {
  groupPhotosByDay,
  useTraineeBodyPhotosList,
} from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/useTraineeMeasurementsQueries";
import { analyticsService } from "~/store/initializeStore";

interface TraineeBodyPhotosProps {
  traineeId: string;
}

const TraineeBodyPhotos = ({ traineeId }: TraineeBodyPhotosProps) => {
  const { t } = useTranslation("trainees");
  const [compareOpen, setCompareOpen] = useState(false);

  const {
    photos: measurements,
    data,
    isLoading,
    isSuccess,
    hasNextPage,
    fetchNextPage,
  } = useTraineeBodyPhotosList(traineeId);
  const photos = useMemo(() => groupPhotosByDay(measurements), [measurements]);
  const photosTotalPages = data?.pages[0]?.totalPages ?? 0;
  // Only the loaded pages are counted; a second page means there are more photos anyway.
  const loadedPhotosCount = measurements.reduce(
    (sum, measurement) => sum + (measurement.id === "-1" ? 0 : (measurement.data.content?.length ?? 0)),
    0,
  );
  const canCompare = loadedPhotosCount >= 2 || photosTotalPages > 1;

  const handleOnLeadMore = () => {
    void fetchNextPage();
  };

  const openCompare = () => {
    analyticsService.track("trainer_body_photos_compare_opened", { traineeId });
    setCompareOpen(true);
  };

  const renderShowMoreButton = () => {
    const showFetchMoreButton = Boolean(hasNextPage) && isSuccess;

    if (showFetchMoreButton) {
      return (
        <div className="mt-8 flex items-center justify-center p-2.5">
          <Button onClick={handleOnLeadMore}>{t("measurements.fetchMore")}</Button>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="mb-2 flex justify-end">
        <Button icon={<SwapOutlined />} disabled={!canCompare} onClick={openCompare}>
          {t("photos.compare.button")}
        </Button>
      </div>
      <List
        itemLayout="horizontal"
        locale={{
          emptyText: (
            <Empty
              imageStyle={{ marginTop: 80 }}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("photos.emptyState")}
            />
          ),
        }}
        loading={isLoading}
        rowKey={(row) => row.date}
        loadMore={renderShowMoreButton()}
        dataSource={photos}
        renderItem={(row) => <TraineeBodyPhotoRow {...row} />}
      />
      {compareOpen ? (
        <TraineeBodyPhotosCompareModal traineeId={traineeId} onClose={() => setCompareOpen(false)} />
      ) : null}
    </>
  );
};

export default TraineeBodyPhotos;
